/**
 * LlPinPromptModal (QGS v2)
 *
 * Shared Line Leader signature dialog used by the scan-flow approve /
 * reject actions on open KOs, closure requests and reworks. Collects
 * {username, pin} and hands them to the parent via onSubmit — the
 * parent is responsible for the actual approve/reject HTTP call so the
 * modal stays endpoint-agnostic.
 *
 * Props:
 *   isOpen      bool
 *   onClose     ()=>void
 *   onSubmit    async ({username, pin}) => void   // throws on failure
 *   title       string (defaults to "Line Leader signature")
 *   subtitle    string | node (optional context line)
 *   ctaLabel    string (defaults to "Approve")
 *   ctaColor    css color token for the primary button (default green)
 *   requireReason bool — when true, also collect a free-text reason
 *                      (used for Reject). onSubmit then gets {username, pin, reason}.
 */
import { useEffect, useRef, useState } from 'react';

export default function LlPinPromptModal({
  isOpen,
  onClose,
  onSubmit,
  title = 'Line Leader signature',
  subtitle = '',
  ctaLabel = 'Approve',
  ctaColor = '#22a06b',
  requireReason = false,
}) {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const pinRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setUsername('');
      setPin('');
      setReason('');
      setSubmitting(false);
      setErrorMsg('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErrorMsg('');
    if (!username.trim() || !pin.trim()) {
      setErrorMsg('Username and PIN are required.');
      return;
    }
    if (requireReason && !reason.trim()) {
      setErrorMsg('Reason is required for rejection.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        username: username.trim(),
        pin: pin.trim(),
        ...(requireReason ? { reason: reason.trim() } : {}),
      });
    } catch (err) {
      const status = err.status;
      const body = err.body || {};
      if (status === 401) {
        const left = body.attempts_left;
        if (body.locked_now) {
          setErrorMsg('This PIN is now locked. Ask an admin to reset.');
        } else {
          setErrorMsg('Incorrect PIN.' + (typeof left === 'number' ? ` ${left} attempts left.` : ''));
        }
      } else if (status === 403) {
        setErrorMsg(body.error || 'Not authorised.');
      } else if (status === 423) {
        setErrorMsg('PIN is locked for this user. Ask an admin to reset.');
      } else {
        setErrorMsg(body.error || err.message || 'Request failed.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,34,55,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={e => e.stopPropagation()}
        autoComplete="off"
        style={{
          background: '#fff', borderRadius: 12, width: 420, maxWidth: '100%',
          padding: 24, boxShadow: '0 10px 30px rgba(15,34,55,0.25)',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        {/* Decoy inputs so browser autofill lands here, not on the real
            LL fields below. */}
        <input type="text" name="username" autoComplete="username" tabIndex={-1}
               style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
        <input type="password" name="password" autoComplete="current-password" tabIndex={-1}
               style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />

        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: '#17253d' }}>{title}</h2>
          {subtitle && (
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#4a596b' }}>{subtitle}</p>
          )}
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#4a596b' }}>Line Leader username</span>
          <input
            type="text"
            name="ll_approver_username"
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
            data-lpignore="true" data-form-type="other"
            autoFocus
            value={username}
            onChange={e => setUsername(e.target.value)}
            disabled={submitting}
            style={{
              padding: '10px 12px', border: '1px solid #cfd6de', borderRadius: 6,
              fontSize: 14, background: '#fff',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#4a596b' }}>PIN (4-6 digits)</span>
          <input
            ref={pinRef}
            type="password"
            inputMode="numeric"
            name="ll_approver_pin"
            autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck={false}
            data-lpignore="true" data-form-type="other"
            maxLength={6}
            value={pin}
            onChange={e => setPin(e.target.value.replace(/[^\d]/g, ''))}
            disabled={submitting}
            style={{
              padding: '10px 12px', border: '1px solid #cfd6de', borderRadius: 6,
              fontSize: 20, letterSpacing: 8, textAlign: 'center', background: '#fff',
            }}
          />
        </label>

        {requireReason && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#4a596b' }}>Reason</span>
            <textarea
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              disabled={submitting}
              style={{
                padding: '8px 10px', border: '1px solid #cfd6de', borderRadius: 6,
                fontSize: 13, background: '#fff', resize: 'vertical',
              }}
            />
          </label>
        )}

        {errorMsg && (
          <div style={{
            padding: '8px 10px', borderRadius: 6, background: '#fce8e6',
            border: '1px solid #f5b7b1', color: '#8a2c22', fontSize: 12,
          }}>{errorMsg}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '10px 16px', border: '1px solid #cfd6de', borderRadius: 6,
              background: '#fff', color: '#4a596b', fontSize: 13, fontWeight: 600,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >Cancel</button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '10px 18px', border: 'none', borderRadius: 6,
              background: submitting ? '#8ab89f' : ctaColor, color: '#fff',
              fontSize: 13, fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >{submitting ? 'Working…' : ctaLabel}</button>
        </div>
      </form>
    </div>
  );
}
